import type { Session } from "@supabase/supabase-js";
import type { GuestCheckoutDetails, GuestCheckoutDraftDetails } from "@workspace/api-client-react";

const API_BASE = `${import.meta.env.BASE_URL || "/"}api`;

export interface GuestCheckoutInfo {
  id: string;
  flowId: string;
  userId: number;
  email: string;
  firstName: string;
  lastName: string;
  createdAt: string;
  expiresAt: string;
  claimed: boolean;
  csrfToken: string;
}

export async function guestCheckoutRequest<T>(
  path: string, options: { token?: string; body?: unknown; csrfToken?: string; signal?: AbortSignal; method?: "PATCH" } = {},
): Promise<T> {
  const controller = new AbortController();
  const abort = () => controller.abort();
  if (options.signal?.aborted) controller.abort();
  else options.signal?.addEventListener("abort", abort, { once: true });
  const timer = window.setTimeout(abort, 15_000);
  try {
  const response = await fetch(`${API_BASE}/guest-checkout${path}`, {
    method: options.method ?? (options.body === undefined ? "GET" : "POST"),
    credentials: "same-origin", signal: controller.signal,
    headers: {
      "Content-Type": "application/json",
      ...(options.token ? { Authorization: `Bearer ${options.token}` } : {}),
      ...(options.csrfToken ? { "X-Checkout-Guest-Csrf": options.csrfToken } : {}),
    },
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  });
  const data = await response.json();
  if (!response.ok) {
    const error = new Error(typeof data.error === "string" ? data.error : "Please try again.") as Error & { status: number; retryAfter: number };
    error.status = response.status;
    error.retryAfter = Math.max(0, Number(response.headers.get("Retry-After")) || 0);
    throw error;
  }
  return data as T;
  } finally {
    window.clearTimeout(timer);
    options.signal?.removeEventListener("abort", abort);
  }
}

export async function beginGuestCheckout(details: GuestCheckoutDetails): Promise<{ session: Session; guest: GuestCheckoutInfo }> {
  const current = await guestCheckoutRequest<{ guest: GuestCheckoutInfo | null }>("");
  const { getTurnstileToken } = await import("./turnstile");
  const turnstileToken = await getTurnstileToken();
  return guestCheckoutRequest("", { body: { ...details, turnstileToken }, csrfToken: current.guest?.csrfToken });
}

/** Contact/recovery only: deliberately returns no Auth/SDK session. */
export async function saveGuestCheckoutDraft(details: GuestCheckoutDraftDetails): Promise<GuestCheckoutInfo> {
  const current = await guestCheckoutRequest<{ guest: GuestCheckoutInfo | null }>("");
  const { getTurnstileToken } = await import("./turnstile");
  const turnstileToken = await getTurnstileToken();
  const saved = await guestCheckoutRequest<{ guest: GuestCheckoutInfo }>("/draft", {
    body: { ...details, turnstileToken }, csrfToken: current.guest?.csrfToken,
  });
  return saved.guest;
}

export async function endGuestCheckout(expectedFlow?: string): Promise<void> {
  const current = await guestCheckoutRequest<{ guest: GuestCheckoutInfo | null }>("");
  if (expectedFlow && current.guest?.flowId !== expectedFlow) return;
  await guestCheckoutRequest("/logout", { body: {}, csrfToken: current.guest?.csrfToken });
}
